import { ButtonT1 } from "../../atoms/Button";

/**
 * Contained card fallback shown by ModuleErrorBoundary when a single
 * lazy-loaded module crashes. The sidebar and navbar remain fully intact.
 *
 * @param {{
 *   moduleName: (string|undefined),
 *   error: (Error|*),
 *   isChunkError: (boolean|undefined),
 *   onReset: function(): void
 * }} props
 * @return {React.ReactElement}
 */
const ModuleErrorFallback = ({ moduleName, error, isChunkError = false, onReset }) => {
  const handleReload = () => {
    window.location.reload();
  };

  const displayName = moduleName ? `${moduleName} module` : "this module";

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {/* Icon */}
        <div style={styles.iconWrapper}>
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M1 21H23L12 2L1 21ZM13 18H11V16H13V18ZM13 14H11V10H13V14Z" fill="#D69E2E" />
          </svg>
        </div>

        {/* Title */}
        <h2 style={styles.title}>
          {isChunkError ? "Application Update Available" : "Something went wrong"}
        </h2>

        {/* Description */}
        <p style={styles.description}>
          {isChunkError ? (
            <>
              A new version of Orderlink was deployed. Please reload the page to load the latest
              components for the <span style={styles.moduleName}>{displayName}</span>.
            </>
          ) : (
            <>
              An unexpected error occurred in the{" "}
              <span style={styles.moduleName}>{displayName}</span>. The rest of the application is
              still running normally.
            </>
          )}
        </p>

        {/* Error details */}
        {error && (
          <details style={styles.details}>
            <summary style={styles.detailsSummary}>Error details</summary>
            <pre style={styles.errorText}>{error.message}</pre>
          </details>
        )}

        {/* Hint */}
        <p style={styles.hint}>
          {isChunkError ? (
            <>
              Click <strong>Reload Page</strong> to refresh the application with the latest update.
            </>
          ) : (
            <>
              Click <strong>Try Again</strong> to reload this module, or{" "}
              <strong>Reload Page</strong> for a full refresh.
            </>
          )}
        </p>

        {/* Actions */}
        <div style={styles.actions}>
          {!isChunkError && (
            <ButtonT1
              onClickCallback={onReset}
              $variant="primary"
              title={
                <>
                  {/* Retry icon */}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: 6 }}
                    aria-hidden="true"
                  >
                    <path
                      d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z"
                      fill="currentColor"
                    />
                  </svg>
                  Try Again
                </>
              }
            />
          )}

          <ButtonT1
            onClickCallback={handleReload}
            $variant={isChunkError ? "primary" : "primaryLight"}
            title="Reload Page"
          />
        </div>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    padding: "24px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "36px 32px",
    maxWidth: "440px",
    width: "100%",
    textAlign: "center",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 6px rgba(0, 0, 0, 0.04)",
    border: "1px solid rgba(214, 158, 46, 0.2)",
  },
  iconWrapper: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
    border: "1px solid #fde68a",
  },
  title: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1a202c",
    margin: "0 0 10px",
    letterSpacing: "-0.2px",
  },
  description: {
    fontSize: "13px",
    color: "#718096",
    lineHeight: "1.7",
    margin: "0 0 20px",
  },
  moduleName: {
    fontWeight: "600",
    color: "#4a5568",
  },
  details: {
    textAlign: "left",
    background: "#f7fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    padding: "10px 14px",
    marginBottom: "16px",
    cursor: "pointer",
  },
  detailsSummary: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#4a5568",
    userSelect: "none",
  },
  errorText: {
    margin: "6px 0 0",
    fontSize: "11px",
    color: "#c05621",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
  },
  hint: {
    fontSize: "12px",
    color: "#a0aec0",
    margin: "0 0 20px",
    lineHeight: "1.6",
  },
  actions: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
};

export { ModuleErrorFallback };
